import type { VariantProps } from 'class-variance-authority';
import * as React from 'react';
import { cn } from '@savvy-kit/lib/utils';
import { cva } from 'class-variance-authority';
import { ArrowDownIcon, ArrowUpIcon } from 'lucide-react';

const tableVariants = cva(
  'w-full caption-bottom text-sm tabular-nums [&_th]:p-2 [&_td]:p-2 [&_td:has([data-cell-type=number])]:text-right',
  {
    variants: {
      size: {
        sm: '[&_th]:h-9 [&_td]:h-9',
        default: '[&_th]:h-[52px] [&_td]:h-[52px]',
        md: '[&_th]:h-[72px] [&_td]:h-[72px]',
        lg: '[&_th]:h-[96px] [&_td]:h-[96px]',
      },
    },
    defaultVariants: {
      size: 'default',
    },
  },
);

export type TableProps = React.ComponentProps<'table'> &
  VariantProps<typeof tableVariants> & {
    readonly borderless?: boolean;
    readonly containerClassName?: string;
  };

function Table({ className, size, borderless = false, containerClassName, ...props }: TableProps) {
  return (
    <div data-slot="table-container" className={cn('relative w-full overflow-x-auto', containerClassName)}>
      <table
        data-slot="table"
        className={cn(tableVariants({ size }), borderless && '[&_tbody_tr]:border-0', className)}
        {...props}
      />
    </div>
  );
}

function TableHeader({ className, ...props }: React.ComponentProps<'thead'>) {
  return (
    <thead
      data-slot="table-header"
      className={cn('[&_tr]:border-b border-light uppercase [&_tr:hover]:bg-transparent bg-slate-100', className)}
      {...props}
    />
  );
}

type SortDirection = 'ASC' | 'DESC';

type SortableHeaderContentProps = React.ComponentProps<'div'> & {
  readonly sortDirection?: SortDirection;
  readonly isSorted?: boolean;
  /**
   * Horizontal alignment of the label within the header cell. Use `center` or
   * `right` to match a column whose cells are centered (status chips, avatars)
   * or right-aligned (numerics, action clusters). Defaults to `left`.
   */
  readonly align?: 'left' | 'center' | 'right';
};

function SortableHeaderContent({
  className,
  children,
  sortDirection,
  isSorted,
  align = 'left',
  onClick,
  onKeyDown,
  ...props
}: SortableHeaderContentProps) {
  const isInteractive = onClick !== undefined;
  const indicator = (
    <div className="flex items-center justify-center">
      {isSorted && sortDirection === 'ASC' && <ArrowUpIcon className="size-4" />}
      {isSorted && sortDirection === 'DESC' && <ArrowDownIcon className="size-4" />}
      {!isSorted && <ArrowUpIcon className="size-4 opacity-0 group-hover:opacity-100" />}
    </div>
  );
  return (
    <div
      className={cn(
        'group flex items-center gap-1 cursor-pointer',
        align === 'center' && 'justify-center',
        align === 'right' && 'justify-end',
        isSorted && 'text-dark',
        className,
      )}
      role={isInteractive ? 'button' : undefined}
      tabIndex={isInteractive ? 0 : undefined}
      onClick={onClick}
      onKeyDown={(event) => {
        onKeyDown?.(event);
        if (isInteractive && (event.key === 'Enter' || event.key === ' ')) {
          event.preventDefault();
          onClick(event as unknown as React.MouseEvent<HTMLDivElement>);
        }
      }}
      {...props}
    >
      {align === 'right' && indicator}
      <span>{children}</span>
      {align !== 'right' && indicator}
    </div>
  );
}

type SortableTableHeadProps = React.ComponentProps<'th'> & {
  readonly sortDirection?: SortDirection;
  readonly isSorted?: boolean;
  readonly contentAlign?: 'left' | 'center' | 'right';
};

const HEAD_TEXT_ALIGN_CLASS = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
} as const;

function SortableTableHead({
  className,
  children,
  sortDirection,
  isSorted,
  contentAlign = 'left',
  onClick,
  ...props
}: SortableTableHeadProps) {
  return (
    <th
      data-slot="table-head"
      className={cn(
        'text-secondary text-xs align-middle font-medium whitespace-nowrap [&:has([role=checkbox])]:pr-0 [&:has([role=checkbox])]:pl-3 [&>[role=checkbox]]:translate-y-[2px]',
        HEAD_TEXT_ALIGN_CLASS[contentAlign],
        className,
      )}
      aria-sort={isSorted ? (sortDirection === 'ASC' ? 'ascending' : 'descending') : 'none'}
      {...props}
    >
      <SortableHeaderContent sortDirection={sortDirection} isSorted={isSorted} align={contentAlign} onClick={onClick}>
        {children}
      </SortableHeaderContent>
    </th>
  );
}

function TableBody({ className, ...props }: React.ComponentProps<'tbody'>) {
  return <tbody data-slot="table-body" className={cn('[&_tr:last-child]:border-0', className)} {...props} />;
}

function TableFooter({ className, ...props }: React.ComponentProps<'tfoot'>) {
  return (
    <tfoot
      data-slot="table-footer"
      className={cn(
        'bg-slate-100 border-t border-light font-medium [&>tr]:last:border-b-0 [&_tr:hover]:bg-transparent',
        className,
      )}
      {...props}
    />
  );
}

function TableRow({ className, ...props }: React.ComponentProps<'tr'>) {
  return (
    <tr
      data-slot="table-row"
      className={cn('hover:bg-table-hover border-b border-light transition-colors', className)}
      {...props}
    />
  );
}

function TableHead({ className, ...props }: React.ComponentProps<'th'>) {
  return (
    <th
      data-slot="table-head"
      className={cn(
        'text-secondary text-xs text-left align-middle font-medium whitespace-nowrap [&:has([role=checkbox])]:pr-0 [&:has([role=checkbox])]:pl-3 [&>[role=checkbox]]:translate-y-[2px]',
        className,
      )}
      {...props}
    />
  );
}

function TableCell({ className, ...props }: React.ComponentProps<'td'>) {
  return (
    <td
      data-slot="table-cell"
      className={cn(
        'align-middle whitespace-nowrap [&:has([role=checkbox])]:pr-0 [&:has([role=checkbox])]:pl-3 [&>[role=checkbox]]:translate-y-[2px]',
        className,
      )}
      {...props}
    />
  );
}

function TableCaption({ className, ...props }: React.ComponentProps<'caption'>) {
  return (
    <caption data-slot="table-caption" className={cn('text-muted-foreground mt-4 text-sm', className)} {...props} />
  );
}

export {
  SortableHeaderContent,
  SortableTableHead,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
};
export type { SortableHeaderContentProps, SortableTableHeadProps, SortDirection };
